import {
  parsePersianNumber,
  toPersianNumericDisplay,
  toLatinDigits,
} from "../Page/LatestServices/components/jalaliUtils";

export { parsePersianNumber, toPersianNumericDisplay, toLatinDigits };

/**
 * Reusable mileage logic (single source of truth for Periodic-style flows).
 *
 * - Persian/English digit support, Persian digit display and 3-digit
 *   thousands separators are inherited from `jalaliUtils` helpers.
 * - No backend calls here: pure functions only, safe to reuse anywhere.
 */

// Default gap between the entered Current Mileage and the suggested
// Next Service Mileage.
export const NEXT_SERVICE_INTERVAL_KM = 10000;

/** Current + 10,000. Returns a Number, or null when input is not usable. */
export function computeDefaultNextMileage(currentMileage) {
  const current =
    typeof currentMileage === "number"
      ? currentMileage
      : parsePersianNumber(currentMileage);
  if (!Number.isFinite(current) || current < 0) return null;
  return current + NEXT_SERVICE_INTERVAL_KM;
}

/** Display string (Persian digits + separators) for Current + 10,000, or "". */
export function formatDefaultNextMileage(currentMileage) {
  const next = computeDefaultNextMileage(currentMileage);
  if (next === null) return "";
  return toPersianNumericDisplay(String(next));
}

/**
 * True when a filled Next Service Mileage is lower than the entered
 * Current Mileage. Empty/invalid values never count (separate validation
 * handles those) so auto-init and untouched fields can't false-positive.
 */
export function isNextMileageBelowCurrent(currentMileage, nextMileage) {
  const current =
    typeof currentMileage === "number"
      ? currentMileage
      : parsePersianNumber(currentMileage);
  if (!Number.isFinite(current)) return false;
  const nextRaw =
    typeof nextMileage === "number"
      ? String(nextMileage)
      : toLatinDigits(nextMileage ?? "").replace(/\D/g, "");
  if (nextRaw === "") return false;
  const next =
    typeof nextMileage === "number" ? nextMileage : parsePersianNumber(nextMileage);
  if (!Number.isFinite(next)) return false;
  return next < current;
}

/**
 * True only when the entered Current Mileage is strictly lower than the
 * vehicle's stored mileage — the single condition that must open the
 * overwrite confirmation popup. Equal / higher / empty / invalid values
 * return false.
 */
export function isEnteredMileageBelowVehicle(enteredMileage, vehicleMileage) {
  const entered =
    typeof enteredMileage === "number"
      ? enteredMileage
      : parsePersianNumber(enteredMileage);
  const stored = Number(vehicleMileage);
  if (!Number.isFinite(entered) || !Number.isFinite(stored)) return false;
  return entered < stored;
}

/** True when the raw mileage input holds at least one digit. */
export function hasMileageValue(raw) {
  return toLatinDigits(raw ?? "").replace(/\D/g, "") !== "";
}
